import React from 'react';

import {useI18n} from '@udemy/i18n';
import {Button} from '@udemy/react-core-components';
import {RadioBlock} from '@udemy/react-form-components';

import {AT_RISK_STEP_ID, OPTION_TO_STEP_ID, STEP_PROGRESS} from '../constants';
import {trackAtRiskHelpOptionSelected} from '../events';
import {StepLayout} from '../step-layout.react-component';
import styles from './initial-step.module.less';

import type {AtRiskStepId, StepRendererProps} from '../types';

export function InitialStep({
    state,
    dispatch,
    onClose,
    submitAnswer,
    source,
    answerOptions,
}: StepRendererProps) {
    const {gettext} = useI18n();

    const handleNext = async () => {
        if (!state.selectedOption) return;
        trackAtRiskHelpOptionSelected(state.selectedOption, source);

        dispatch({type: 'SUBMITTING'});
        try {
            await submitAnswer(state.selectedOption);
            const nextStepId = OPTION_TO_STEP_ID[state.selectedOption];
            if (!nextStepId) {
                // pretty_good: no response step, close immediately
                onClose();
            } else {
                dispatch({type: 'STEP_ADVANCED', nextStepId: nextStepId as AtRiskStepId});
            }
        } catch {
            dispatch({
                type: 'SUBMIT_ERROR',
                error: gettext('Something went wrong. Please try again.'),
            });
        }
    };

    return (
        <StepLayout
            progressValue={STEP_PROGRESS[AT_RISK_STEP_ID.INITIAL]}
            onClose={onClose}
            footerRightChildren={
                <Button
                    udStyle="primary"
                    size="medium"
                    disabled={!state.selectedOption || state.isSubmitting}
                    onClick={handleNext}
                >
                    {state.isSubmitting ? gettext('Submitting…') : gettext('Next')}
                </Button>
            }
        >
            <div className={styles['content']}>
                {state.submitError && (
                    <p className={styles['error']} role="alert">
                        {state.submitError}
                    </p>
                )}
                <div
                    role="radiogroup"
                    aria-label={gettext("How's your learning going?")}
                    className={styles['options']}
                >
                    {answerOptions.map((option) => (
                        <RadioBlock
                            key={option.textCode}
                            name="at-risk-feedback"
                            value={option.textCode}
                            checked={state.selectedOption === option.textCode}
                            onChange={() =>
                                dispatch({type: 'OPTION_SELECTED', value: option.textCode})
                            }
                        >
                            {option.text}
                        </RadioBlock>
                    ))}
                </div>
            </div>
        </StepLayout>
    );
}
